import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Lock, Play, Clock, Barbell, CheckCircle, Heart } from "@phosphor-icons/react";
import { api, thumbUrl, videoUrl, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ClassDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cls, setCls] = useState(null);
  const [access, setAccess] = useState({ has: false, checked: false });
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    api.get(`/classes/${id}`).then((r) => setCls(r.data)).catch(() => {});
    api.get("/paypal/config").then((r) => setPaypalCfg(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!cls) return;
    if (cls.is_free) { setAccess({ has: true, checked: true }); return; }
    if (!user) { setAccess({ has: false, checked: true }); return; }
    api.get("/library").then((r) => {
      const hasSub = r.data.has_subscription;
      const owned = r.data.classes.some((c) => c.id === id);
      setAccess({ has: hasSub || owned, checked: true });
    }).catch(() => setAccess({ has: false, checked: true }));
  }, [user, cls, id]);

  if (!cls) return <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500 label-overline" data-testid="loading">Loading…</div>;

  const hasAccess = access.has || user?.role === "admin";
  const singlePrice = paypalCfg?.single_class_price || "4.00";

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="class-detail-page">
      <Link to="/classes" className="label-overline hover:text-[#D4A574]" data-testid="back-to-classes">← Back</Link>

      <div className="mt-6 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="relative aspect-video bg-black border border-white/10" data-testid="video-container">
            {hasAccess ? (
              <video
                src={videoUrl(id)}
                controls
                className="w-full h-full"
                data-testid="class-video-player"
              />
            ) : (
              <>
                <img src={thumbUrl(id)} alt="" className="w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-testid="paywall-overlay">
                  <Lock size={48} weight="duotone" className="text-[#D4A574]" />
                  <span className="label-overline text-white">Locked — Purchase to unlock</span>
                </div>
              </>
            )}
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="label-overline">// {cls.category}</span>
              {cls.is_free && (
                <span className="label-overline bg-[#D4A574] text-black px-2 py-1 flex items-center gap-1" data-testid="free-badge-detail">
                  <Heart size={12} weight="fill" /> Free Class
                </span>
              )}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-widest leading-none mt-3" data-testid="class-title">{cls.title}</h1>
            <div className="flex gap-6 mt-5 text-sm text-zinc-300">
              <span className="flex items-center gap-2"><Clock size={16} />{cls.duration_min} Min</span>
              <span className="flex items-center gap-2"><Barbell size={16} />{cls.difficulty}</span>
            </div>
            <p className="mt-8 text-zinc-300 leading-relaxed whitespace-pre-wrap" data-testid="class-description">{cls.description}</p>
          </div>
        </div>

        <aside className="border border-white/10 p-6 h-fit sticky top-24" data-testid="purchase-sidebar">
          {hasAccess ? (
            <div className="text-center" data-testid="access-granted">
              <CheckCircle size={40} weight="duotone" className="text-[#D4A574] mx-auto" />
              <p className="font-display text-2xl tracking-wider mt-3">{cls.is_free ? "Free Class" : "Unlocked"}</p>
              <p className="text-sm text-zinc-400 mt-2">{cls.is_free ? "Press play to start." : "You have access to this class."}</p>
              <button
                onClick={() => document.querySelector('[data-testid="class-video-player"]')?.play()}
                className="kinetic-btn w-full mt-6 flex items-center justify-center gap-2"
                data-testid="play-class-btn"
              >
                <Play size={16} weight="fill" /> Play Class
              </button>
            </div>
          ) : (
            <>
              <span className="label-overline">// Buy 1 class</span>
              <div className="font-display text-5xl text-[#D4A574] mt-3" data-testid="class-price">£{Number(singlePrice).toFixed(2)}</div>
              <p className="text-sm text-zinc-400 mt-2">Lifetime access to this class.</p>

              {!user ? (
                <button onClick={() => nav("/login")} className="kinetic-btn w-full mt-6" data-testid="login-to-buy-btn">
                  Login to Purchase
                </button>
              ) : paypalCfg?.client_id ? (
                <div className="mt-6" data-testid="paypal-buttons-wrapper">
                  <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: paypalCfg.currency, intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "black", shape: "rect", label: "pay" }}
                      disabled={purchasing}
                      createOrder={async () => {
                        try {
                          setPurchasing(true);
                          const { data } = await api.post("/paypal/orders", { class_id: id });
                          return data.id;
                        } catch (e) {
                          toast.error(formatErr(e));
                          setPurchasing(false);
                          throw e;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          await api.post(`/paypal/orders/${data.orderID}/capture`, { class_id: id });
                          toast.success("Purchase complete. Class unlocked.");
                          setAccess({ has: true, checked: true });
                        } catch (e) {
                          toast.error(formatErr(e));
                        } finally {
                          setPurchasing(false);
                        }
                      }}
                      onCancel={() => { setPurchasing(false); toast("Checkout cancelled"); }}
                      onError={(err) => { console.error(err); setPurchasing(false); toast.error("PayPal error"); }}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <p className="text-xs text-zinc-500 mt-6">PayPal not configured.</p>
              )}

              <div className="border-t border-white/10 mt-8 pt-6">
                <span className="label-overline">// Or get All-Access</span>
                <p className="text-sm text-zinc-300 mt-2">Unlock every class for £{paypalCfg?.subscription_price || "14.99"}/mo.</p>
                <Link to="/subscribe" className="label-overline mt-4 inline-block border border-white/20 px-4 py-2 hover:border-[#D4A574] hover:text-[#D4A574]" data-testid="go-subscribe-btn">
                  Subscribe
                </Link>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
